import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { Preparacion } from '../componentes/Preparacion';

interface FilaDelInforme {
  readonly linea: number;
  readonly desenlace: 'crear' | 'omitir' | 'error';
  readonly clave: string;
  readonly motivo?: string;
}

interface Vista {
  readonly codificacion: string;
  readonly separador: string;
  readonly encabezados: readonly string[];
  readonly columnas: Record<string, number | null>;
  readonly leidas: number;
  readonly aCrear: number;
  readonly aOmitir: number;
  readonly conError: number;
  readonly informe: readonly FilaDelInforme[];
}

interface Lote {
  readonly id: string;
  readonly entidad: string;
  readonly estado: string;
  readonly nombreDelArchivo: string;
  readonly filasCreadas: number;
  readonly filasOmitidas: number;
  readonly filasConError: number;
  readonly creadoEn: string;
  readonly correoDelUsuario: string | null;
}

const TONO = { crear: 'bien', omitir: 'neutro', error: 'mal' } as const;

/**
 * En este orden, y no es alfabético: **es el orden en que hay que importar**. Un precio o una
 * existencia de un artículo que todavía no está no se puede cargar en ningún lado.
 */
const ENTIDADES = ['articulos', 'precios', 'existencias', 'terceros'] as const;
type Entidad = (typeof ENTIDADES)[number];

/** Los bytes del archivo en base64, que es como viajan adentro del JSON. */
async function enBase64(archivo: File): Promise<string> {
  const bytes = new Uint8Array(await archivo.arrayBuffer());
  let binario = '';
  // De a pedazos: pasarle cien mil bytes de una a `fromCharCode` desborda la pila de argumentos.
  for (let desde = 0; desde < bytes.length; desde += 8192) {
    binario += String.fromCharCode(...bytes.subarray(desde, desde + 8192));
  }
  return btoa(binario);
}

/**
 * Importar el catálogo.
 *
 * Lo que esta pantalla tiene que hacer bien es una sola cosa: **que nadie confirme sin saber qué va
 * a pasar**. Se elige el archivo, se ve el informe completo —cuántas entran, cuántas se omiten,
 * cuáles no entran y en qué línea del archivo— y recién ahí aparece el botón de confirmar.
 *
 * Y una decisión de forma que importa: **el informe muestra primero lo que no entró**. Lo que entra
 * bien no hay que mirarlo; lo que no entró es lo que alguien va a tener que corregir a mano, y
 * hacerlo buscar entre tres mil renglones verdes es hacerle perder la tarde.
 */
export function Importacion(): React.ReactElement {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const textoDeError = useTextoDeError();

  const [entidad, setEntidad] = useState<Entidad>('articulos');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [vista, setVista] = useState<Vista | null>(null);
  const [contenido, setContenido] = useState<string>('');
  const [aplicado, setAplicado] = useState<Vista | null>(null);

  const lotes = useQuery({
    queryKey: ['lotes-de-importacion'],
    queryFn: () => pedir<Lote[]>('/importacion/lotes'),
  });

  const previsualizar = useMutation({
    mutationFn: async (elegido: File) => {
      const base64 = await enBase64(elegido);
      setContenido(base64);
      return pedir<Vista>('/importacion/vista-previa', {
        method: 'POST',
        cuerpo: { entidad, nombreDelArchivo: elegido.name, contenido: base64 },
      });
    },
    onSuccess: (resultado) => {
      setVista(resultado);
      setAplicado(null);
    },
  });

  const revertir = useMutation({
    mutationFn: (loteId: string) =>
      pedir<{ deshechas: number }>(`/importacion/lotes/${loteId}/reversion`, { method: 'POST' }),
    onSuccess: () => {
      void cola.invalidateQueries({ queryKey: ['lotes-de-importacion'] });
      void cola.invalidateQueries({ queryKey: ['preparacion'] });
    },
  });

  const aplicar = useMutation({
    mutationFn: () =>
      pedir<{ loteId: string; vista: Vista }>('/importacion/lotes', {
        method: 'POST',
        cuerpo: { entidad, nombreDelArchivo: archivo?.name ?? 'planilla.csv', contenido },
      }),
    onSuccess: (resultado) => {
      setAplicado(resultado.vista);
      setVista(null);
      setArchivo(null);
      void cola.invalidateQueries({ queryKey: ['lotes-de-importacion'] });
      // Importar cambia lo que le falta al comercio para operar: el aviso de arriba se recalcula.
      void cola.invalidateQueries({ queryKey: ['preparacion'] });
    },
  });

  const columnasDeLotes: Columna<Lote>[] = [
    {
      clave: 'archivo',
      titulo: t('importacion.archivo'),
      contenido: (fila) => fila.nombreDelArchivo,
    },
    {
      clave: 'cuando',
      titulo: t('importacion.cuando'),
      contenido: (fila) => new Date(fila.creadoEn).toLocaleString(),
    },
    { clave: 'quien', titulo: t('importacion.quien'), contenido: (fila) => fila.correoDelUsuario },
    {
      clave: 'resultado',
      titulo: t('importacion.resultado'),
      contenido: (fila) =>
        t('importacion.resumen', {
          creadas: fila.filasCreadas,
          omitidas: fila.filasOmitidas,
          conError: fila.filasConError,
        }),
    },
    {
      clave: 'acciones',
      titulo: '',
      contenido: (fila) =>
        fila.estado === 'revertido' ? (
          <Insignia tono="neutro">{t('importacion.revertido')}</Insignia>
        ) : (
          <Boton
            tono="peligro"
            cargando={revertir.isPending}
            onClick={() => {
              revertir.mutate(fila.id);
            }}
          >
            {t('importacion.deshacer')}
          </Boton>
        ),
    },
  ];

  // Primero lo que no entró: es lo que alguien va a tener que corregir a mano.
  const informeOrdenado = [...(vista?.informe ?? [])].sort((uno, otro) => {
    const peso = { error: 0, omitir: 1, crear: 2 };
    return peso[uno.desenlace] - peso[otro.desenlace] || uno.linea - otro.linea;
  });

  return (
    <section className="flex flex-col gap-4">
      <Encabezado titulo={t('importacion.titulo')} descripcion={t('importacion.descripcion')} />

      {/* La misma pregunta del inicio, contestada acá: **¿qué le falta a este comercio para
          operar?** Es la guía de la puesta en marcha, y no es una pantalla aparte porque quien
          importa es exactamente quien está resolviendo esa lista. */}
      <Preparacion />

      <Tarjeta>
        {/* Qué se importa y en qué orden. Los cuatro botones van en el orden en que hay que
            usarlos, y debajo se dice por qué: un precio de un artículo que no está no entra. */}
        <div className="mb-3 flex flex-wrap gap-1">
          {ENTIDADES.map((cual) => (
            <Boton
              key={cual}
              tono={entidad === cual ? 'principal' : 'secundario'}
              onClick={() => {
                setEntidad(cual);
                setArchivo(null);
                setVista(null);
                setAplicado(null);
              }}
            >
              {t(`importacion.entidades.${cual}`)}
            </Boton>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            key={entidad}
            type="file"
            accept=".csv,text/csv,text/plain"
            aria-label={t('importacion.elegirArchivo')}
            onChange={(evento) => {
              const elegido = evento.target.files?.[0] ?? null;
              setArchivo(elegido);
              setVista(null);
              setAplicado(null);
              if (elegido !== null) previsualizar.mutate(elegido);
            }}
            className="text-sm"
          />
          {previsualizar.isPending ? (
            <span className="text-xs text-texto-tenue">{t('importacion.leyendo')}</span>
          ) : null}
        </div>
        <p className="mt-2 text-xs text-texto-tenue">{t(`importacion.ayudas.${entidad}`)}</p>
        <p className="mt-1 text-xs text-texto-tenue">{t('importacion.elOrden')}</p>
        {previsualizar.isError ? (
          <p className="mt-2 text-xs text-mal">{textoDeError(previsualizar.error)}</p>
        ) : null}
      </Tarjeta>

      {aplicado === null ? null : (
        <Tarjeta>
          <p className="flex items-center gap-2 text-sm">
            <Insignia tono="bien">{t('importacion.listo')}</Insignia>
            {t('importacion.resumen', {
              creadas: aplicado.aCrear,
              omitidas: aplicado.aOmitir,
              conError: aplicado.conError,
            })}
          </p>
        </Tarjeta>
      )}

      {vista === null ? null : (
        <Tarjeta>
          <h2 className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold">
            {t('importacion.vistaPrevia')}
            <Insignia tono="bien">{t('importacion.aCrear', { total: vista.aCrear })}</Insignia>
            {vista.aOmitir === 0 ? null : (
              <Insignia tono="neutro">
                {t('importacion.aOmitir', { total: vista.aOmitir })}
              </Insignia>
            )}
            {vista.conError === 0 ? null : (
              <Insignia tono="mal">{t('importacion.conError', { total: vista.conError })}</Insignia>
            )}
          </h2>

          {/* Cómo se leyó el archivo. No es un dato técnico de más: si dice que el separador es la
              coma y el archivo usa punto y coma, se ve acá y no después de importar mal. */}
          <p className="text-xs text-texto-tenue">
            {t('importacion.comoSeLeyo', {
              codificacion: vista.codificacion,
              separador: vista.separador === '\t' ? 'tab' : vista.separador,
              columnas: vista.encabezados.join(', '),
            })}
          </p>

          <div className="mt-3 max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-superficie">
                <tr className="text-left text-xs text-texto-tenue">
                  <th className="pb-1">{t('importacion.linea')}</th>
                  <th className="pb-1">{t('importacion.clave')}</th>
                  <th className="pb-1">{t('importacion.desenlace')}</th>
                </tr>
              </thead>
              <tbody>
                {informeOrdenado.map((fila) => (
                  <tr key={`${String(fila.linea)}-${fila.clave}`} className="border-t border-borde">
                    <td className="py-1 numero">{fila.linea}</td>
                    <td className="py-1">{fila.clave}</td>
                    <td className="py-1">
                      <Insignia tono={TONO[fila.desenlace]}>
                        {t(`importacion.desenlaces.${fila.desenlace}`)}
                      </Insignia>{' '}
                      {fila.motivo === undefined ? null : (
                        <span className="text-xs text-texto-tenue">
                          {t(`errores:${fila.motivo}`, { defaultValue: fila.motivo })}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Boton
              tono="principal"
              cargando={aplicar.isPending}
              disabled={vista.aCrear === 0}
              onClick={() => {
                aplicar.mutate();
              }}
            >
              {t('importacion.confirmar', { total: vista.aCrear })}
            </Boton>
            <span className="text-xs text-texto-tenue">{t('importacion.nadaSeEscribio')}</span>
          </div>
          {aplicar.isError ? (
            <p className="mt-2 text-xs text-mal">{textoDeError(aplicar.error)}</p>
          ) : null}
        </Tarjeta>
      )}

      <Tarjeta>
        <h2 className="mb-2 text-sm font-semibold">{t('importacion.anteriores')}</h2>
        {revertir.isError ? (
          <p className="mb-2 text-xs text-mal">{textoDeError(revertir.error)}</p>
        ) : null}
        {revertir.data === undefined ? null : (
          <p className="mb-2 text-xs text-texto-tenue">
            {t('importacion.deshecho', { total: revertir.data.deshechas })}
          </p>
        )}
        {lotes.isError ? <Problema error={lotes.error} /> : null}
        {(lotes.data ?? []).length === 0 ? (
          <Vacio titulo={t('importacion.sinLotes')} />
        ) : (
          <Tabla
            filas={lotes.data ?? []}
            columnas={columnasDeLotes}
            claveDeFila={(fila) => fila.id}
          />
        )}
      </Tarjeta>
    </section>
  );
}
